import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Clock, Volume2, VolumeX, ChefHat, CheckCircle2, Utensils, Trash2, Ban } from "lucide-react";
import { CancelOrderDialog } from "@/components/admin/CancelOrderDialog";
import { DeleteOrderDialog } from "@/components/admin/DeleteOrderDialog";
import { money } from "@/lib/currency";
import { orderNo } from "@/lib/order";
import { lineTotal } from "@/lib/addons";
import { LineAddons } from "@/components/LineAddons";
import { orderChime, unlockAudio } from "@/lib/beep";
import { toast } from "sonner";

const FLOW = { pending: "preparing", preparing: "ready", ready: "delivered" };
const CLOSED = ["delivered", "cancelled"];
const COLUMNS = [
  { key: "pending", label: "Pending", color: "amber", action: "Start preparing" },
  { key: "preparing", label: "Preparing", color: "blue", action: "Mark ready" },
  { key: "ready", label: "Ready", color: "emerald", action: "Mark delivered" },
];
const ring = { amber: "border-amber-500/50", blue: "border-blue-500/50", emerald: "border-emerald-500/50" };
const dot = { amber: "bg-amber-500", blue: "bg-blue-500", emerald: "bg-emerald-500" };
const btn = { amber: "bg-amber-600 hover:bg-amber-700", blue: "bg-blue-600 hover:bg-blue-700", emerald: "bg-emerald-600 hover:bg-emerald-700" };

const timeAgo = (iso) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export const OrdersBoard = ({ currency, canDelete = true }) => {
  const [orders, setOrders] = useState([]);
  const [sound, setSound] = useState(true);
  const [cancelling, setCancelling] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const seen = useRef(null);

  const load = async () => {
    let data;
    try {
      ({ data } = await api.get("/my/orders"));
    } catch {
      return;
    }
    const fresh = seen.current ? data.filter((o) => o.status === "pending" && !seen.current.has(o.id)) : [];
    if (fresh.length) {
      if (sound) orderChime();
      toast.info(fresh.length > 1 ? `${fresh.length} new orders received` : `New order · Table ${fresh[0].table_number}`);
    }
    seen.current = new Set(data.map((o) => o.id));
    setOrders(data);
  };

  useEffect(() => {
    document.addEventListener("pointerdown", unlockAudio, { once: true });
    return () => document.removeEventListener("pointerdown", unlockAudio);
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [sound]);

  const advance = async (order) => {
    const next = FLOW[order.status];
    await api.put(`/my/orders/${order.id}/status`, { status: next });
    toast.success(`Order #${orderNo(order)} → ${next}`);
    load();
  };

  const activeOrders = orders.filter((o) => !CLOSED.includes(o.status));

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-6">
        <div className="min-w-0">
          <h2 className="font-heading text-2xl font-bold">Live kitchen board</h2>
          <p className="text-slate-400 text-sm">{activeOrders.length} active orders · auto-refreshing</p>
        </div>
        <button data-testid="toggle-sound-button" onClick={() => { if (!sound) orderChime(); setSound((s) => !s); }} className="inline-flex items-center gap-2 shrink-0 whitespace-nowrap bg-slate-900 border border-slate-800 hover:border-slate-600 rounded-xl px-3 md:px-4 py-2.5 text-sm transition-colors">
          {sound ? <Volume2 className="h-4 w-4 text-orange-400" /> : <VolumeX className="h-4 w-4 text-slate-500" />}
          {sound ? "Sound on" : "Sound off"}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {COLUMNS.map((col) => {
          const colOrders = orders.filter((o) => o.status === col.key);
          return (
            <div key={col.key} data-testid={`kds-column-${col.key}`}>
              <div className="flex items-center gap-2 mb-3">
                <span className={`h-2.5 w-2.5 rounded-full ${dot[col.color]}`} />
                <h3 className="font-sub font-semibold uppercase text-sm tracking-wide">{col.label}</h3>
                <span className="text-xs text-slate-500">({colOrders.length})</span>
              </div>
              <div className="space-y-4">
                {colOrders.length === 0 && (
                  <div className="border border-dashed border-slate-800 rounded-xl p-6 text-center text-slate-600 text-sm">No orders</div>
                )}
                {colOrders.map((o) => (
                  <div key={o.id} data-testid={`order-card-${o.id}`} className={`bg-slate-900 border-2 ${ring[col.color]} rounded-xl p-5 fade-up ${col.key === "pending" ? "pulse-pending" : ""}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Utensils className="h-4 w-4 text-orange-400" />
                        <span className="font-sub font-bold">Table {o.table_number}</span>
                      </div>
                      <span data-testid={`order-number-${o.id}`} className="font-mono-j text-xs text-slate-400">#{orderNo(o)}</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-slate-400 mt-1">
                      <Clock className="h-3 w-3" /> {timeAgo(o.created_at)}
                    </div>
                    <div className="mt-3 space-y-1.5 border-t border-slate-800 pt-3">
                      {o.items.map((it, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span><span className="font-semibold text-orange-400">{it.qty}×</span> {it.name}<LineAddons addons={it.addons} cur={currency} className="text-amber-300" /></span>
                          <span className="text-slate-500 font-mono-j">{money(lineTotal(it), currency)}</span>
                        </div>
                      ))}
                    </div>
                    {o.note && <p className="text-xs text-amber-400 mt-2 italic">“{o.note}”</p>}
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-800">
                      <span className="font-mono-j font-bold text-lg">{money(o.total, currency)}</span>
                      <button data-testid={`advance-order-${o.id}`} onClick={() => advance(o)} className={`inline-flex items-center gap-1.5 ${btn[col.color]} rounded-lg px-3 py-2 text-sm font-semibold transition-colors`}>
                        {col.key === "ready" ? <CheckCircle2 className="h-4 w-4" /> : <ChefHat className="h-4 w-4" />} {col.action}
                      </button>
                    </div>
                    <div className={`grid gap-2 mt-2 ${canDelete ? "grid-cols-2" : "grid-cols-1"}`}>
                      <button data-testid={`cancel-order-${o.id}`} onClick={() => setCancelling(o)} className="inline-flex items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-semibold text-amber-300 bg-amber-950/30 border border-amber-900/60 hover:bg-amber-950/60 transition-colors">
                        <Ban className="h-3.5 w-3.5" /> Cancel order
                      </button>
                      {canDelete && (
                        <button data-testid={`delete-order-${o.id}`} onClick={() => setDeleting(o)} className="inline-flex items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-semibold text-red-300 bg-red-950/30 border border-red-900/60 hover:bg-red-950/60 transition-colors">
                          <Trash2 className="h-3.5 w-3.5" /> Delete
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {cancelling && <CancelOrderDialog order={cancelling} onClose={() => setCancelling(null)} onDone={() => { setCancelling(null); load(); }} />}
      {deleting && <DeleteOrderDialog order={deleting} currency={currency} onClose={() => setDeleting(null)} onDone={() => { setDeleting(null); load(); }} />}
    </div>
  );
};
